import { CheckIcon, DownloadIcon, SaveIcon } from 'lucide-react'

import { EmployerRenewalsRoute } from 'apps/employer_renewals/routes'
import { Button, ButtonLink, Tooltip } from 'slab'
import { cn } from 'utils'
import { type QuotingQuoteQuoteType } from 'utils/web_api.schemas'

import { QuoteExplorerMode, type WorkflowType } from './QuoteExplorer'
import { ShareQuoteButtonModal } from './ShareQuoteButtonModal'
import ShareRenewalQuote from './ShareRenewalQuote'

export const ActionButtons = (
  {
    acceptQuoteUrl,
    canOnboardFromQuote,
    canShareQuote,
    discardChanges,
    downloadLink,
    hasUnsavedChanges,
    onClickSaveQuote,
    prospectName,
    prospectAssociatedWithEmployer,
    quoteId,
    mode,
    saveButtonDisabled,
    hideDownloadLink = false,
    canSendEditorInvites,
    canSendOnboardInvites,
    workflowType,
    quoteType,
  }:
  {
    acceptQuoteUrl: string | null,
    canOnboardFromQuote: boolean,
    canShareQuote: boolean,
    discardChanges: () => void,
    downloadLink: string | null,
    hasUnsavedChanges: boolean,
    onClickSaveQuote: () => void,
    prospectName: string,
    prospectAssociatedWithEmployer: boolean
    quoteId: string,
    mode: QuoteExplorerMode,
    saveButtonDisabled: boolean,
    hideDownloadLink?: boolean,
    canSendEditorInvites: boolean,
    canSendOnboardInvites: boolean,
    workflowType?: WorkflowType,
    quoteType?: QuotingQuoteQuoteType,
  }
) => (
  mode === QuoteExplorerMode.explore
    ? (
      <>
        {canOnboardFromQuote && (
          <Tooltip
            content='This quote has already been applied to a Thatch account. If this is unexpected, reach out to your broker or Thatch sales representative.'
            enabled={prospectAssociatedWithEmployer}
          >
            <div>
              <ButtonLink
                variant='primary'
                width='full'
                href={acceptQuoteUrl ?? '#'}
                disabled={prospectAssociatedWithEmployer}
              >
                <div className='flex items-center'>
                  <CheckIcon className='w-4 h-4' />
                  <span className='ml-2'>Save and create Thatch account</span>
                </div>
              </ButtonLink>
            </div>
          </Tooltip>
        )}
        {workflowType === 'renewal' && (
          <ButtonLink
            variant='primary'
            width='full'
            href={EmployerRenewalsRoute.ALLOWANCES_CONFIRM}
          >
            Continue renewal
          </ButtonLink>
        )}
        {canShareQuote && (
          <div
            className={cn(
              'grid',
              {
                // when there's an onboarding button it should be stacked side by side with the share button
                'grid-cols-2 gap-2': canOnboardFromQuote,
                // otherwise they should be stacked on top of each other
                'grid-cols-1 space-x-0 space-y-2': !canOnboardFromQuote,
              }
            )}
          >
            {quoteType === 'renewal'
              ? <ShareRenewalQuote />
              : <ShareQuoteButtonModal
                  buttonVariant={canOnboardFromQuote ? 'secondary' : 'primary'}
                  quoteId={quoteId}
                  prospectName={prospectName}
                  hasUnsavedChanges={hasUnsavedChanges}
                  onSaveChanges={onClickSaveQuote}
                  onDiscardChanges={discardChanges}
                  isSavingChanges={saveButtonDisabled}
                  canSendEditorInvites={canSendEditorInvites}
                  canSendOnboardInvites={canSendOnboardInvites}
                />}
            {!hideDownloadLink && (
              <ButtonLink href={downloadLink || '#'} width='full' disabled={!downloadLink} download>
                <div className='flex items-center'>
                  <DownloadIcon className='w-4 h-4 text-gray-500' />
                  <span className='ml-2'>Download</span>
                </div>
              </ButtonLink>
            )}
          </div>
        )}
        {!canShareQuote && (
          <ButtonLink href={downloadLink || '#'} width='full' disabled={!downloadLink} download>
            <div className='flex items-center'>
              <DownloadIcon className='w-4 h-4 text-gray-500' />
              <span className='ml-2'>Download</span>
            </div>
          </ButtonLink>
        )}
      </>
      )
    : (
      <>
        {hasUnsavedChanges
          ? (
            <Button
              variant='primary'
              onClick={onClickSaveQuote}
              disabled={saveButtonDisabled}
              width='full'
              data-testid='save-edits-button'
            >
              <div className='flex items-center'>
                <SaveIcon className='w-4 h-4 text-white' />
                <span className='ml-2'>Save edits</span>
              </div>
            </Button>
            )
          : (
            <Tooltip content='No changes to save' delayDuration={100}>
              <div className='w-full cursor-not-allowed'>
                <Button
                  variant='primary'
                  onClick={onClickSaveQuote}
                  width='full'
                  disabled
                  data-testid='save-edits-button'
                >
                  <div className='flex items-center'>
                    <SaveIcon className='w-4 h-4 text-white' />
                    <span className='ml-2'>Save edits</span>
                  </div>
                </Button>
              </div>
            </Tooltip>
            )}
        <Button
          variant={hasUnsavedChanges ? 'tertiary' : 'tertiary-deemphasized'}
          onClick={discardChanges}
          disabled={!hasUnsavedChanges}
        >
          Discard changes
        </Button>
      </>
      )
)

export default ActionButtons
